import { MantineSize } from '@mantine/core';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { abbreviateString, formatServiceJobLabel } from 'src/utils';

export interface IChargeCodeCellProps {
  /** Whether the owning timesheet's job is a service job. */
  isServiceJob: boolean;
  /** Service work order call id (GP Service_Call_ID); rendered first, never abbreviated. */
  serviceCallId?: string | null;
  /** Service work order description (linked snapshot or manual freeform text). */
  serviceDescription?: string | null;
  /** Service work order location (GP Adrscode). */
  serviceLocation?: string | null;
  /** Charge code label for non-service (CTC) rows — e.g. cost code, account name, or WBS path. */
  fallback?: React.ReactNode;
  /** Optional max lengths to abbreviate the description/location for width-constrained tables. */
  abbreviate?: { description?: number; location?: number };
  /** Text size for the rendered charge code. */
  size?: MantineSize;
}

const abbreviateOrSelf = (value: string, max?: number): string =>
  max ? abbreviateString(value, max) : value;

/**
 * Single source of truth for rendering a timesheet row's charge code cell.
 *
 * Service rows show the work order call id, location, and description joined by
 * dashes as one continuous string so it wraps naturally; non-service rows render
 * the supplied {@link fallback}. Each call site resolves its own non-service
 * charge code (cost code, account name, or WBS path) and passes it in, so only
 * the service presentation is shared.
 * @registry Renders a timesheet row's charge code cell — service call id/location/description for service jobs, or a caller-supplied fallback for CTC rows.
 */
export const ChargeCodeCell: React.FC<IChargeCodeCellProps> = ({
  isServiceJob,
  serviceCallId,
  serviceDescription,
  serviceLocation,
  fallback,
  abbreviate,
  size = 'sm',
}) => {
  if (!isServiceJob) {
    return (
      <Text c="bright" size={size}>
        {fallback || 'N/A'}
      </Text>
    );
  }

  const description = serviceDescription
    ? abbreviateOrSelf(serviceDescription, abbreviate?.description)
    : '';
  const location = serviceLocation
    ? abbreviateOrSelf(serviceLocation, abbreviate?.location)
    : '';

  if (!serviceCallId && !description && !location) {
    return (
      <Text size={size} c="dimmed">
        Service
      </Text>
    );
  }

  // Single continuous string (not a flex Group) so the cell wraps mid-line.
  // Call id leads and is never abbreviated; absent parts drop out cleanly.
  const label = formatServiceJobLabel({
    jobNumber: serviceCallId,
    address: location,
    jobName: description,
  });

  return (
    <Text c="bright" size={size}>
      {label}
    </Text>
  );
};
